import { useMemo, useState } from "react";
import { useQuery } from "react-query";
import { api } from "@/lib/api";
import { useAuth } from "@/context/auth.context";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import ReportPageWrapper, { StatCard } from "./ReportPageWrapper";
import {
  CheckCircle,
  DollarSign,
  Calendar,
  AlertTriangle,
  Landmark,
  TrendingUp,
  Download,
  Printer,
  Search,
  RotateCcw,
  FileText,
} from "lucide-react";
import { format, subMonths } from "date-fns";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ComposedChart, Legend,
} from "recharts";

function CustomTooltip({ active, payload, label }: any) {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white border border-slate-200 rounded-lg shadow-lg p-3 text-sm">
        <p className="font-medium text-slate-700 mb-1">{label}</p>
        {payload.map((entry: any, idx: number) => (
          <p key={idx} style={{ color: entry.color }} className="text-xs">
            {entry.name}: {entry.value?.toLocaleString()} FRW
          </p>
        ))}
      </div>
    );
  }
  return null;
}

export default function RepaymentsReport() {
  const { user } = useAuth();
  const [search, setSearch] = useState("");

  const { data: loansData, isLoading } = useQuery(
    ["my-loans-repayments", user?.id],
    async () => {
      const { data: res } = await api.get(`/teachers/${user?.id}/loans`);
      const d = res.data || res;
      return {
        loans: Array.isArray(d.loans) ? d.loans : [],
        totalBorrowed: d.totalBorrowed || 0,
        totalRepaid: d.totalRepaid || 0,
        outstandingBalance: d.outstandingBalance || 0,
      };
    },
    { enabled: !!user?.id }
  );

  const loans = loansData?.loans || [];

  const allPayments = useMemo(() => {
    const payments: any[] = [];
    loans.forEach((loan: any) => {
      if (loan.payments) {
        loan.payments.forEach((p: any) => {
          payments.push({ ...p, loanAmount: loan.amount, loanPurpose: loan.purpose, loanStatus: loan.status });
        });
      }
    });
    return payments.sort((a, b) => new Date(b.paymentDate || b.createdAt).getTime() - new Date(a.paymentDate || a.createdAt).getTime());
  }, [loans]);

  const filtered = useMemo(() => {
    if (!search) return allPayments;
    return allPayments.filter((p: any) =>
      (p.referenceNumber || "").toLowerCase().includes(search.toLowerCase()) ||
      (p.paymentMethod || "").toLowerCase().includes(search.toLowerCase())
    );
  }, [allPayments, search]);

  const stats = useMemo(() => {
    const totalPaid = allPayments.reduce((s: number, p: any) => s + (parseFloat(p.amount) || 0), 0);
    const totalBorrowed = loansData?.totalBorrowed || loans.reduce((s: number, l: any) => s + (l.amount || 0), 0);
    const outstanding = loansData?.outstandingBalance || loans
      .filter((l: any) => ["Active", "Approved"].includes(l.status))
      .reduce((s: number, l: any) => s + (l.dueAmount || l.amount || 0), 0);
    const nextPayment = loans
      .filter((l: any) => ["Active", "Approved"].includes(l.status))
      .reduce((s: number, l: any) => s + (l.monthlyPayment || 0), 0);
    const overdue = loans
      .filter((l: any) => l.status === "Defaulted")
      .reduce((s: number, l: any) => s + (l.dueAmount || l.amount || 0), 0);
    const progress = totalBorrowed > 0 ? (totalPaid / totalBorrowed) * 100 : 0;
    return { totalPaid, totalBorrowed, outstanding, nextPayment, overdue, progress };
  }, [allPayments, loans, loansData]);

  const progressData = useMemo(() => {
    const months: Record<string, { paid: number; expected: number }> = {};
    const now = new Date();
    for (let i = 11; i >= 0; i--) {
      const d = subMonths(now, i);
      const key = format(d, "MMM yy");
      months[key] = { paid: 0, expected: 0 };
    }
    allPayments.forEach((p: any) => {
      const d = new Date(p.paymentDate || p.createdAt);
      const key = format(d, "MMM yy");
      if (months[key]) months[key].paid += parseFloat(p.amount || p.principalAmount) || 0;
    });
    loans.forEach((l: any) => {
      if (l.monthlyPayment && l.status === "Active") {
        const d = new Date(l.applicationDate || l.createdAt);
        for (let i = 0; i < (l.termMonths || 1); i++) {
          const monthDate = new Date(d.getFullYear(), d.getMonth() + i, 1);
          const key = format(monthDate, "MMM yy");
          if (months[key]) months[key].expected += l.monthlyPayment;
        }
      }
    });
    return Object.entries(months).map(([label, vals]) => ({ label, ...vals }));
  }, [allPayments, loans]);

  const formatCurrency = (val: number) => (val || 0).toLocaleString() + " FRW";

  const handleReset = () => { setSearch(""); };

  const handleExport = () => {
    const headers = ["Date", "Amount", "Method", "Reference", "Loan", "Balance After", "Status"];
    const rows = filtered.map((p: any) => [
      format(new Date(p.paymentDate || p.createdAt), "yyyy-MM-dd"),
      parseFloat(p.amount) || 0, p.paymentMethod || "N/A",
      p.referenceNumber || "N/A", p.loanPurpose || "N/A",
      p.balanceAfter || 0, p.isVerified ? "Verified" : "Pending",
    ]);
    const csv = [headers, ...rows].map(r => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `repayments-report-${format(new Date(), "yyyy-MM-dd")}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  if (isLoading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-8 w-48 bg-slate-200 rounded" />
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {[1,2,3,4,5,6].map(i => <div key={i} className="h-20 bg-slate-100 rounded-xl" />)}
        </div>
        <div className="h-64 bg-slate-100 rounded-xl" />
      </div>
    );
  }

  return (
    <ReportPageWrapper
      title="Repayments Report"
      breadcrumbItems={[
        { label: "Dashboard", path: "/member/dashboard" },
        { label: "Member Reports", path: "/member/reports" },
        { label: "Repayments Report" },
      ]}
    >
      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <StatCard label="Total Paid" value={formatCurrency(stats.totalPaid)} icon={CheckCircle} color="#009A44" />
        <StatCard label="Remaining Balance" value={formatCurrency(stats.outstanding)} icon={DollarSign} color="#EF4444" />
        <StatCard label="Next Payment Due" value={formatCurrency(stats.nextPayment)} icon={Calendar} color="#EAB308" />
        <StatCard label="Overdue Amount" value={formatCurrency(stats.overdue)} icon={AlertTriangle} color="#DC2626" />
        <StatCard label="Total Borrowed" value={formatCurrency(stats.totalBorrowed)} icon={Landmark} color="#3B82F6" />
        <StatCard label="Repayment Progress" value={stats.progress.toFixed(1) + "%"} icon={TrendingUp} color="#009A44" />
      </div>

      {/* Progress Bar */}
      <div className="bg-white border border-slate-200 shadow-sm rounded-xl p-5">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-slate-700">Overall Repayment Progress</span>
          <span className="text-sm font-bold text-[#009A44]">{stats.progress.toFixed(1)}%</span>
        </div>
        <div className="w-full bg-slate-100 rounded-full h-4 overflow-hidden">
          <div
            className="h-full rounded-full transition-all duration-500"
            style={{ width: `${Math.min(100, stats.progress)}%`, backgroundColor: "#009A44" }}
          />
        </div>
        <div className="flex justify-between text-xs text-slate-500 mt-1.5">
          <span>{formatCurrency(stats.totalPaid)} repaid</span>
          <span>{formatCurrency(stats.totalBorrowed)} total borrowed</span>
        </div>
      </div>

      {/* Chart */}
      <div className="bg-white border border-slate-200 shadow-sm rounded-xl p-5">
        <h3 className="text-sm font-semibold text-slate-800 mb-4 flex items-center gap-2">
          <TrendingUp className="h-4 w-4 text-[#009A44]" />
          Monthly Repayment Progress
        </h3>
        {progressData.some(d => d.paid > 0 || d.expected > 0) ? (
          <div className="h-52">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={progressData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="label" tick={{ fontSize: 10 }} stroke="#94a3b8" />
                <YAxis tick={{ fontSize: 10 }} stroke="#94a3b8" tickFormatter={(v) => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v} />
                <Tooltip content={<CustomTooltip />} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="expected" name="Expected" fill="#EAB308" radius={[3, 3, 0, 0]} opacity={0.6} />
                <Bar dataKey="paid" name="Paid" fill="#009A44" radius={[3, 3, 0, 0]} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="text-center py-8 bg-slate-50 rounded-lg">
            <TrendingUp className="h-8 w-8 text-slate-300 mx-auto mb-2" />
            <p className="text-sm text-slate-500">No repayment data yet</p>
          </div>
        )}
      </div>

      {/* Repayment Table Card */}
      <div className="bg-white border border-slate-200 shadow-sm rounded-xl overflow-hidden">
        {/* Toolbar */}
        <div className="p-4 border-b border-slate-100">
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[180px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search repayments..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="pl-9 h-9 text-sm"
              />
            </div>
            <Button variant="ghost" size="sm" onClick={handleReset} className="text-slate-500 h-9">
              <RotateCcw className="h-3.5 w-3.5 mr-1" /> Reset
            </Button>
            <Button variant="outline" size="sm" onClick={() => window.print()} className="h-9">
              <Printer className="h-3.5 w-3.5 mr-1" /> Print
            </Button>
            <Button variant="outline" size="sm" onClick={handleExport} className="h-9">
              <Download className="h-3.5 w-3.5 mr-1" /> Export
            </Button>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#009A44]">
                <th className="text-left px-4 py-3 text-xs font-semibold text-white uppercase tracking-wider">Date</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-white uppercase tracking-wider">Amount</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-white uppercase tracking-wider">Method</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-white uppercase tracking-wider">Reference</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-white uppercase tracking-wider">Loan</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-white uppercase tracking-wider">Balance</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-white uppercase tracking-wider">Status</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length > 0 ? filtered.slice(0, 50).map((p: any, idx: number) => (
                <tr key={p.id || idx} className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors">
                  <td className="px-4 py-3 text-slate-700 whitespace-nowrap">
                    {format(new Date(p.paymentDate || p.createdAt), "MMM dd, yyyy")}
                  </td>
                  <td className="px-4 py-3 text-right font-medium text-slate-900">
                    {(parseFloat(p.amount) || 0).toLocaleString()} FRW
                  </td>
                  <td className="px-4 py-3 text-slate-600">{p.paymentMethod || "N/A"}</td>
                  <td className="px-4 py-3 text-slate-500 font-mono text-xs max-w-[100px] truncate">
                    {p.referenceNumber || "N/A"}
                  </td>
                  <td className="px-4 py-3 text-slate-600 max-w-[100px] truncate">{p.loanPurpose || "N/A"}</td>
                  <td className="px-4 py-3 text-right text-slate-600">
                    {(p.balanceAfter || 0).toLocaleString()} FRW
                  </td>
                  <td className="px-4 py-3">
                    <Badge
                      variant={p.isVerified ? "success" : "secondary"}
                      className="text-[10px]"
                    >
                      {p.isVerified ? "Verified" : "Pending"}
                    </Badge>
                  </td>
                </tr>
              )) : (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-sm text-slate-500">
                    No repayment records found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </ReportPageWrapper>
  );
}
